/* ====================================================================
 *  variantDisplay — pure-хелпери derived-стану варіанта (бейджі,
 *  наявність, кількість). Без Vue і без I/O.
 *
 *  Споживачі: ProductCard, useProductVariants, wishlist, catalog,
 *  checkout, cart.
 *
 *  Дві РІЗНІ семантики, не плутати:
 *    readAvailability   — чи є товар фізично (сток/прапорці наявності);
 *    isSellableVariant  — чи можна це продати (ще й опубліковано).
 *  Каталог показує лише опубліковане, тому йому вистачає першої;
 *  кошик і чекаут мають знати про зняте з продажу — їм потрібна друга.
 * ==================================================================== */

type AnyRecord = Record<string, unknown>;

const NUM_QTY_KEYS = [
  "stock",
  "stockQty",
  "qty",
  "quantity",
  "inventory",
  "inventoryQty",
] as const;

const BOOL_AVAILABILITY_KEYS = [
  "inStock",
  "isInStock",
  "available",
  "isAvailable",
  "isActive",
] as const;

const BOOL_PUBLISH_KEYS = [
  "published",
  "isPublished",
  "isPublic",
  "isVisible",
  "visible",
] as const;

/** Ексклюзивний лот: явний прапорець, тип або кастомний текст бейджа. */
export function isExclusiveVariant(v: unknown): boolean {
  if (!v || typeof v !== "object") return false;
  const o = v as AnyRecord;
  return o.isExclusive === true || !!o.exclusiveType || !!o.badgeText;
}

/** Текст бейджа ексклюзиву (без гарду isExclusiveVariant — гард на боці викликача). */
export function exclusiveBadgeText(v: unknown): string {
  if (!v || typeof v !== "object") return "Ексклюзив";
  const o = v as AnyRecord;
  if (o.badgeText) return String(o.badgeText);
  const t = String(o.exclusiveType || "");
  if (t === "one_of_one") return "1 з 1";
  if (t === "defect") return "З дефектом";
  if (t === "sample") return "Зразок";
  if (t === "limited") return "Ліміт";
  return "Ексклюзив";
}

/**
 * Текст бейджа для CART-ITEM (снапшот полів варіанта, зроблений при
 * додаванні в кошик: badgeText/isExclusive/exclusiveType). Тексти НАВМИСНО
 * відрізняються від exclusiveBadgeText (рівень варіанта на картці/сторінці
 * товару): кошик/чекаут історично показують "Ексклюзив: Дефект/Семпл/Лімітка".
 * Уніфікація формулювань — окреме продуктове рішення, не рефакторинг.
 */
export function cartItemBadgeText(it: unknown): string {
  if (!it || typeof it !== "object") return "";
  const o = it as AnyRecord;

  const explicit = String(o.badgeText || "").trim();
  if (explicit) return explicit;

  if (!o.isExclusive) return "";

  const t = String(o.exclusiveType || "").trim();
  const map: Record<string, string> = {
    one_of_one: "1 з 1",
    defect: "Дефект",
    sample: "Семпл",
    limited: "Лімітка",
  };

  return t ? `Ексклюзив: ${map[t] || t}` : "Ексклюзив";
}

/** Кількість на складі — ТІЛЬКИ числові інвентарні поля, без bool-фолбеків. */
export function readVariantQty(obj: unknown): number | null {
  if (!obj || typeof obj !== "object") return null;
  const o = obj as AnyRecord;
  for (const k of NUM_QTY_KEYS) {
    if (k in o && typeof o[k] === "number") return o[k] as number;
  }
  return null;
}

/**
 * Best-effort наявність по одному об'єкту (product АБО variant):
 * спершу явні bool-прапорці, потім числовий сток (>0). null = сигналів немає.
 */
export function readAvailability(obj: unknown): boolean | null {
  if (!obj || typeof obj !== "object") return null;
  const o = obj as AnyRecord;
  for (const k of BOOL_AVAILABILITY_KEYS) {
    if (k in o && typeof o[k] === "boolean") return o[k] as boolean;
  }
  const qty = readVariantQty(obj);
  if (qty !== null) return qty > 0;
  return null;
}

/** Наявність пари product+variant: variant має пріоритет, без сигналів — true. */
export function isItemInStock(product: unknown, variant: unknown): boolean {
  const vAvail = readAvailability(variant);
  if (vAvail !== null) return vAvail;
  const pAvail = readAvailability(product);
  if (pAvail !== null) return pAvail;
  return true;
}

/**
 * Чи опублікований товар/варіант. Крім плоских прапорців дивиться
 * історичні вкладені форми (`status.published` тощо). null = сигналів немає.
 */
export function readPublished(obj: unknown): boolean | null {
  if (!obj || typeof obj !== "object") return null;
  const o = obj as AnyRecord;

  for (const k of BOOL_PUBLISH_KEYS) {
    if (k in o && typeof o[k] === "boolean") return o[k] as boolean;
  }

  const nested = [
    (o.status as AnyRecord)?.published,
    (o.meta as AnyRecord)?.published,
    (o.visibility as AnyRecord)?.published,
    (o.visibility as AnyRecord)?.isPublished,
    (o.visibility as AnyRecord)?.isVisible,
  ];
  for (const v of nested) {
    if (typeof v === "boolean") return v;
  }

  return null;
}

/**
 * Чи можна варіант продати: знятий з публікації — не можна незалежно
 * від залишку; далі — звичайна наявність. null = сигналів немає.
 *
 * Відрізняється від старої cart-версії порядком: там прапорці наявності
 * перевірялись ПЕРЕД published, тож варіант `{inStock:true,
 * published:false}` вважався доступним. Це виправлено свідомо —
 * знятий з продажу не продається.
 */
export function isSellableVariant(obj: unknown): boolean | null {
  if (readPublished(obj) === false) return false;
  return readAvailability(obj);
}

/** Значення кольору з обох історичних форм: рядок або `{ value }`. */
export function getColorValue(c: unknown): string {
  if (typeof c === "string") return c;
  if (!c || typeof c !== "object") return "";
  return String((c as AnyRecord).value || "");
}

/**
 * Чи варіант ще «живий» відносно самого товару: опублікований, і його
 * колір/розмір досі присутні у списках товару (адмін міг прибрати колір,
 * лишивши старий документ варіанта). Порожній список у товару = обмежень
 * немає, перевірка пропускається.
 *
 * Свідомо вужче за `readPublished`: дивиться ЛИШЕ плоский `published`,
 * бо саме така семантика була в каталозі, а розширення на вкладені форми
 * змінило б видачу. Для «чи можна продати» є `isSellableVariant`.
 */
export function isVariantValid(product: unknown, variant: unknown): boolean {
  if (!variant || typeof variant !== "object") return false;
  const v = variant as AnyRecord;
  if (v.published === false) return false;

  const p = (product || {}) as AnyRecord;
  const colors = Array.isArray(p.colors) ? p.colors.map(getColorValue) : [];
  const sizes = Array.isArray(p.sizes) ? p.sizes.map(String) : [];

  // Свідомо НЕ через getColorValue: історична перевірка порівнювала сирий
  // fallback, тож варіант із поламаним кольором (`{}` без `value`) не
  // збігався НІ з чим — навіть із порожнім рядком у списку товару.
  // Зведення його до "" зробило б такі варіанти видимими в каталозі.
  const vColor = ((v.color as AnyRecord | null)?.value || v.color || "") as
    string | undefined;
  const colorOk = colors.length ? colors.includes(vColor as string) : true;
  const sizeOk = sizes.length ? sizes.includes(String(v.size || "")) : true;

  return colorOk && sizeOk;
}
